import React, { ReactNode, useEffect, useState } from "react";
import { SearchActivity } from "../../../components/GenericTable/SubComponents/TableHeader/TableSearchBar";
import { getListSearchThrottle } from "../../../components/GenericTable/SubComponents/TableHeader/searchThrottle";
import { isTooShortToSearch } from "../../../../utils/functions/listSearchThrottle";
import { LogoSearchStatus } from "../subcomponents/logo-search-status";

// Search feedback for the logo list (flag REACT_APP_LOGO_SEARCH_FEEDBACK_
// ENABLED). Customer report 14/9: after typing "Big Clean" nothing on screen
// says whether the list is searching - the previous rows only dim (see
// FunctionalList) and a hit count never appears. This turns the react-query
// state the list ALREADY has (isFetching / isPreviousData / totalCount) plus
// the search field's debounce window into three FunctionalList props:
// the spinner inside the field, the status line and the progress bar.
// 0 extra calls; the debounce and the query key are untouched here. With
// the list-search throttle on (flag listSearchThrottleEnabled) a draft
// under the minimum length is not "searching" - the field shows its hint
// instead of the spinner and the status line says the same.
export interface LogoSearchFeedbackInput {
  enabled: boolean;
  searchTerm: string | undefined;
  isFetching: boolean;
  isPreviousData: boolean;
  totalCount?: number;
  pageResultCount: number;
  onClear: () => void;
}

export interface LogoSearchFeedbackProps {
  searchActivity?: SearchActivity;
  searchStatus?: ReactNode;
  showFetchProgress?: boolean;
}

// Flag off: one frozen empty object, so `{...props}` adds nothing at all.
const NONE: LogoSearchFeedbackProps = Object.freeze({});

export function useLogoSearchFeedback({
  enabled,
  searchTerm,
  isFetching,
  isPreviousData,
  totalCount,
  pageResultCount,
  onClear,
}: LogoSearchFeedbackInput): LogoSearchFeedbackProps {
  const term = searchTerm ?? "";
  // Debounce window of the search field (typed, not yet emitted).
  const [draft, setDraft] = useState<string | null>(null);
  // Term whose rows are on screen. With keepPreviousData a fetch for a NEW
  // key shows the old rows: a differing term = search-driven (field spinner
  // + "Searching…"), an equal term = paging/filter within the same search
  // (only the dim + progress bar, and the hit count stays valid).
  const [shownTerm, setShownTerm] = useState(term);
  const loadingNewKey = isFetching && isPreviousData;
  useEffect(() => {
    if (!loadingNewKey) setShownTerm(term);
  }, [loadingNewKey, term]);

  if (!enabled) return NONE;

  // Throttle off: minChars = 0, so nothing is ever too short (= #806 as is).
  const { minChars } = getListSearchThrottle();
  const tooShort = draft !== null && isTooShortToSearch(draft, minChars);
  const searchInFlight = loadingNewKey && term !== shownTerm;
  const isSearching = (draft !== null && !tooShort) || searchInFlight;
  return {
    searchActivity: { isSearching, onDraftChange: setDraft },
    searchStatus: (
      <LogoSearchStatus
        term={draft ?? term}
        isSearching={isSearching}
        minCharsHint={tooShort ? minChars : undefined}
        totalCount={totalCount}
        pageResultCount={pageResultCount}
        onClear={onClear}
      />
    ),
    showFetchProgress: true,
  };
}
